/**
 * Client Matrix Waterfall Chart Component
 *
 * Shows the journey from Receita to Margem.
 * Receita - Custos = Margem
 *
 * Dark theme styled for Monutrack design system.
 */

// Waterfall item definition
export interface ClientWaterfallItemConfig {
  key: string;
  label: string;
  type: 'start' | 'add' | 'subtract' | 'total';
}

// All waterfall items in order (dark theme colors)
export const CLIENT_WATERFALL_ITEMS: ClientWaterfallItemConfig[] = [
  { key: 'receita', label: 'Receita', type: 'start' },
  { key: 'custos', label: 'Custos', type: 'subtract' },
  { key: 'margem', label: 'Margem', type: 'total' },
];

interface ClientMatrixWaterfallProps {
  values: Record<string, number>;
  selectedMetrics: Set<string>;
  height?: number;
}

interface ProcessedWaterfallItem extends ClientWaterfallItemConfig {
  value: number;
  barStart: number;
  barEnd: number;
  runningTotal: number;
}

// Format currency in compact form
function formatCompactCurrency(value: number): string {
  const absValue = Math.abs(value);
  if (absValue >= 1_000_000) {
    return `R$ ${(value / 1_000_000).toFixed(1)}M`;
  }
  if (absValue >= 1_000) {
    return `R$ ${(value / 1_000).toFixed(0)}K`;
  }
  return `R$ ${value.toFixed(0)}`;
}

export function ClientMatrixWaterfall({
  values,
  selectedMetrics,
  height = 260,
}: ClientMatrixWaterfallProps): JSX.Element {
  // Filter to only selected items, preserving order
  const activeItems = CLIENT_WATERFALL_ITEMS.filter(item => selectedMetrics.has(item.key));

  if (activeItems.length === 0) {
    return (
      <div style={{
        padding: '2rem',
        textAlign: 'center',
        color: '#a0a0a0',
        background: '#141414',
        borderRadius: '12px',
        fontFamily: 'Inter, system-ui, sans-serif',
      }}>
        Selecione métricas para exibir na composição
      </div>
    );
  }

  // Build processed items with running totals
  let runningTotal = 0;
  const processedItems: ProcessedWaterfallItem[] = activeItems.map((item) => {
    const rawValue = values[item.key] || 0;
    // For subtract items, ensure value is negative for calculation
    const value = item.type === 'subtract' ? -Math.abs(rawValue) : rawValue;

    if (item.type === 'start') {
      runningTotal = Math.abs(rawValue);
      return {
        ...item,
        value: Math.abs(rawValue),
        barStart: 0,
        barEnd: Math.abs(rawValue),
        runningTotal: Math.abs(rawValue),
      };
    }

    if (item.type === 'total') {
      return {
        ...item,
        value: rawValue,
        barStart: 0,
        barEnd: rawValue,
        runningTotal: rawValue,
      };
    }

    const prevTotal = runningTotal;
    runningTotal += value;

    return {
      ...item,
      value,
      barStart: value >= 0 ? prevTotal : runningTotal,
      barEnd: value >= 0 ? runningTotal : prevTotal,
      runningTotal,
    };
  });

  // Calculate scale
  const allValues = processedItems.flatMap(item => [item.barStart, item.barEnd]);
  const maxValue = Math.max(...allValues, 1);
  const minValue = Math.min(...allValues, 0);
  const range = maxValue - minValue || 1;

  // Chart dimensions
  const paddingLeft = 10;
  const paddingRight = 10;
  const paddingTop = 30;
  const paddingBottom = 50;
  const chartWidth = Math.max(processedItems.length * 120 + paddingLeft + paddingRight, 400);
  const chartHeight = height - paddingTop - paddingBottom;

  const barWidth = Math.min(80, (chartWidth - paddingLeft - paddingRight) / processedItems.length - 20);
  const xStep = (chartWidth - paddingLeft - paddingRight) / processedItems.length;
  const getX = (i: number): number => paddingLeft + i * xStep + xStep / 2;
  const getY = (val: number): number => paddingTop + chartHeight - ((val - minValue) / range) * chartHeight;

  // Get bar color based on type and value (dark theme colors)
  const getBarColor = (item: ProcessedWaterfallItem): string => {
    if (item.type === 'start') return '#3b82f6'; // Blue for Receita
    if (item.type === 'total') return item.value >= 0 ? '#D2FA52' : '#ef4444'; // Monumenta green for positive / red for negative
    if (item.value >= 0) return '#D2FA52'; // Monumenta green for additions
    return '#ef4444'; // Monutrack red for subtractions
  };

  return (
    <div style={{
      background: '#141414',
      borderRadius: '12px',
      padding: '1rem',
      overflowX: 'auto',
      fontFamily: 'Inter, system-ui, sans-serif',
    }}>
      <svg width={chartWidth} height={height} style={{ display: 'block' }}>
        {/* Zero line - dark theme */}
        <line
          x1={paddingLeft}
          y1={getY(0)}
          x2={chartWidth - paddingRight}
          y2={getY(0)}
          stroke="#4a4a4a"
          strokeWidth="1"
        />

        {/* Connecting lines between bars - dark theme */}
        {processedItems.map((item, i) => {
          if (i === 0 || item.type === 'total') return null;
          const prevItem = processedItems[i - 1];

          return (
            <line
              key={`connector-${i}`}
              x1={getX(i - 1) + barWidth / 2}
              y1={getY(prevItem.runningTotal)}
              x2={getX(i) - barWidth / 2}
              y2={getY(prevItem.runningTotal)}
              stroke="#4a4a4a"
              strokeWidth="1"
              strokeDasharray="4"
            />
          );
        })}

        {/* Bars */}
        {processedItems.map((item, i) => {
          const x = getX(i) - barWidth / 2;
          const y1 = getY(item.barStart);
          const y2 = getY(item.barEnd);
          const barHeight = Math.abs(y2 - y1);
          const barTop = Math.min(y1, y2);

          return (
            <g key={item.key}>
              {/* Bar */}
              <rect
                x={x}
                y={barTop}
                width={barWidth}
                height={Math.max(barHeight, 2)}
                fill={getBarColor(item)}
                rx="3"
              />

              {/* Value label - dark theme */}
              <text
                x={x + barWidth / 2}
                y={barTop - 6}
                textAnchor="middle"
                fontSize="11"
                fontWeight="600"
                fill={getBarColor(item)}
                fontFamily='Inter, system-ui, sans-serif'
              >
                {formatCompactCurrency(Math.abs(item.value))}
              </text>

              {/* X-axis label - dark theme */}
              <text
                x={x + barWidth / 2}
                y={height - 15}
                textAnchor="middle"
                fontSize="11"
                fill="#a0a0a0"
                fontWeight="500"
                fontFamily='Inter, system-ui, sans-serif'
              >
                {item.label}
              </text>

              {/* +/- indicator - dark theme */}
              {item.type !== 'start' && item.type !== 'total' && (
                <text
                  x={x + barWidth / 2}
                  y={height - 28}
                  textAnchor="middle"
                  fontSize="10"
                  fill={item.value >= 0 ? '#D2FA52' : '#ef4444'}
                  fontWeight="600"
                  fontFamily='Inter, system-ui, sans-serif'
                >
                  {item.value >= 0 ? '(+)' : '(-)'}
                </text>
              )}
            </g>
          );
        })}

        {/* Legend - dark theme */}
        <g>
          <rect x={chartWidth - 100} y={5} width={10} height={10} fill="#3b82f6" rx="2" />
          <text
            x={chartWidth - 85}
            y={14}
            fontSize="9"
            fill="#a0a0a0"
            fontFamily='Inter, system-ui, sans-serif'
          >
            Receita
          </text>

          <rect x={chartWidth - 100} y={20} width={10} height={10} fill="#ef4444" rx="2" />
          <text
            x={chartWidth - 85}
            y={29}
            fontSize="9"
            fill="#a0a0a0"
            fontFamily='Inter, system-ui, sans-serif'
          >
            Custos
          </text>

          <rect x={chartWidth - 100} y={35} width={10} height={10} fill="#D2FA52" rx="2" />
          <text
            x={chartWidth - 85}
            y={44}
            fontSize="9"
            fill="#a0a0a0"
            fontFamily='Inter, system-ui, sans-serif'
          >
            Margem
          </text>
        </g>
      </svg>
    </div>
  );
}

export default ClientMatrixWaterfall;
